/**
 * How the ship's machinery looks. There are three families (Family), each one
 * look for the whole set so a ship reads as one thing, and every part is a
 * block in each. The engines come in three sizes: small (one block), medium
 * (2x2) and large (3x3). Every engine is drawn exhaust down and turned for the
 * other three ways, each its own block (T turns through them). TileAtlas
 * paints every machine block from here (SHIP_PAINT), and preview.html shows
 * them side by side ("Ship designs").
 */
import type { Family } from "@dyson/shared";
import { BRASS, FIRE, GLASS, IRON, ROCK, ROPE, TILE_PX, WOOD, backWalls, beam, brass, disc, fill, glassPane, grain, line, noise, panel, path, pixels, planks, plate, poly, ring, rivet, shape, stoneField, tone, type Ctx } from "./paint";

export type { Family };

const S = TILE_PX;
type Ramp = readonly string[];

export const FAMILIES: Record<Family, { name: string; blurb: string }> = {
  ironclad: { name: "Ironclad", blurb: "Heavy riveted plate, dark and squared off, handwheel valves and vent slots. A warship's engine room." },
  brassworks: { name: "Brassworks", blurb: "Polished brass boilers with copper hoops, dials and bell-mouthed nozzles. Victorian machinery, kept bright." },
  shipwright: { name: "Shipwright", blurb: "Iron boilers in timber cradles, barrel staves and hoops, rope lashings. Built by a carpenter who learned engines late." },
};

export interface PartDesign {
  family: Family;
  /** Size in tiles. */
  w: number;
  h: number;
  blurb: string;
  paint: (ctx: Ctx) => void;
}

export interface PartGroup {
  id: string;
  title: string;
  note: string;
  designs: PartDesign[];
}

// ----- palettes the families add

const COPPER = ["#4a2616", "#7c4424", "#ad6430", "#d08a4c", "#f2b882"] as const;
/** Darker, bluer iron for armour. */
const ARMOUR = ["#1a1d21", "#2b3036", "#3f454c", "#555c64", "#6f777f"] as const;
const SOOT = "#0f0c0a";
const DARK = "#101214";
const WATER = ["#1d4a8c", "#2a64b4", "#5a9ce0"] as const;

// ----- machine parts

/** A cylinder: standing up (`upright`, round across x) or lying across (round across y), shaded round, lit toward its upper left. */
function drum(ctx: Ctx, x: number, y: number, w: number, h: number, ramp: Ramp, upright: boolean): void {
  pixels(
    ctx,
    w,
    h,
    (px, py) => {
      const u = ((upright ? px : py) + 0.5) / (upright ? w : h);
      return tone(ramp, 0.16 + Math.sin(Math.PI * u) * 0.74 - u * 0.14, px, py);
    },
    x,
    y,
  );
}

/** A hoop round a drum: a band of `ramp` shaded as the drum is, standing a pixel proud, with a shadow line after it. */
function hoop(ctx: Ctx, x: number, y: number, w: number, h: number, ramp: Ramp, upright: boolean): void {
  drum(ctx, x, y, w, h, ramp, upright);
  if (upright) fill(ctx, "#1a1612", x, y + h, w, 1);
  else fill(ctx, "#1a1612", x + w, y, 1, h);
}

/** A nozzle pointing down from (cx, y): a bell `top` wide at its neck flaring to `mouth` wide at the bottom, `h` tall, its mouth black with soot. */
function bell(ctx: Ctx, cx: number, y: number, top: number, mouth: number, h: number, ramp: Ramp = IRON): void {
  const x0 = Math.round(cx - mouth / 2);
  pixels(
    ctx,
    mouth,
    h,
    (px, py) => {
      const t = (py + 0.5) / h;
      const w = top + (mouth - top) * t;
      const left = (mouth - w) / 2;
      if (px + 0.5 < left || px + 0.5 > mouth - left) return null;
      const u = (px + 0.5 - left) / w;
      return tone(ramp, 0.12 + Math.sin(Math.PI * u) * 0.68 - t * 0.12, px, py);
    },
    x0,
    y,
  );
  fill(ctx, SOOT, x0 + 1, y + h - 3, mouth - 2, 3);
  fill(ctx, "#2a2018", x0 + 2, y + h - 3, mouth - 4, 1);
}

/** A round gauge: a brass rim, a pale face, a red needle. */
function dial(ctx: Ctx, cx: number, cy: number, r: number): void {
  disc(ctx, BRASS[0], cx, cy, r);
  disc(ctx, BRASS[3], cx, cy, r - 1);
  disc(ctx, "#ebe4d2", cx, cy, r - 2.2);
  line(ctx, "#b8321c", cx, cy, cx + (r - 2.5) * 0.6, cy - (r - 2.5) * 0.75, 1);
  fill(ctx, "#2a2420", Math.round(cx) - 1, Math.round(cy) - 1, 2, 2);
}

/** Fire behind bars: a black slot with the firebox glowing in it, `bars` iron bars across it. */
function grate(ctx: Ctx, x: number, y: number, w: number, h: number, bars = 2): void {
  fill(ctx, "#0e0e10", x, y, w, h);
  pixels(ctx, w - 2, h - 2, (px, py) => tone(FIRE, 1.2 - Math.hypot((px + 1 - w / 2) / (w / 2), (py + 1 - h * 0.7) / (h * 0.75)) - (noise(px, py, 4, 9) - 0.5) * 0.3, px, py, 0.8), x + 1, y + 1);
  for (let i = 1; i <= bars; i++) {
    const by = y + Math.round((h * i) / (bars + 1)) - 1;
    fill(ctx, IRON[0], x, by, w, 2);
    fill(ctx, IRON[2], x, by, w, 1);
  }
}

/** A round firebox port: fire behind a ring of `rim`, with a cross of bars over it if `barred`. */
function fireEye(ctx: Ctx, cx: number, cy: number, r: number, rim: Ramp = IRON, barred = true): void {
  disc(ctx, rim[0]!, cx, cy, r + 2.5);
  disc(ctx, rim[2]!, cx, cy, r + 1.6);
  shape(ctx, rim[3]!, cx - r - 2, cy - r - 2, cx, cy, (x, y) => {
    const d = Math.hypot(x - cx, y - cy);
    return d > r + 0.8 && d < r + 2.2 && x - cx < -(y - cy) * 0.4;
  });
  const x0 = Math.round(cx - r);
  const y0 = Math.round(cy - r);
  pixels(
    ctx,
    Math.round(r * 2),
    Math.round(r * 2),
    (px, py) => {
      const d = Math.hypot((px + 0.5 - r) / r, (py + 0.5 - r) / r);
      if (d > 1) return null;
      return tone(FIRE, 1.25 - d - (noise(px, py, 4, 3) - 0.5) * 0.3, px, py, 0.8);
    },
    x0,
    y0,
  );
  if (barred) {
    fill(ctx, rim[0]!, Math.round(cx) - 1, y0, 2, Math.round(r * 2));
    fill(ctx, rim[0]!, x0, Math.round(cy) - 1, Math.round(r * 2), 2);
  }
}

/** A plate of armour: a bevelled panel of `ramp` with a rivet in each corner. */
function armour(ctx: Ctx, x: number, y: number, w: number, h: number, ramp: Ramp = ARMOUR): void {
  panel(ctx, x, y, w, h, ramp[2]!, ramp[3]!, ramp[0]!);
  for (const [rx, ry] of [[x + 2, y + 2], [x + w - 4, y + 2], [x + 2, y + h - 4], [x + w - 4, y + h - 4]] as const) rivet(ctx, rx, ry);
}

/** Rivets from (x0, y0) to (x1, y1), about `step` apart. */
function rivets(ctx: Ctx, x0: number, y0: number, x1: number, y1: number, step: number): void {
  const n = Math.max(1, Math.round(Math.hypot(x1 - x0, y1 - y0) / step));
  for (let i = 0; i <= n; i++) rivet(ctx, Math.round(x0 + ((x1 - x0) * i) / n), Math.round(y0 + ((y1 - y0) * i) / n));
}

/** A dark vent slot with a lit rim: air in, nothing burning behind it. */
function vent(ctx: Ctx, x: number, y: number, w: number, h: number): void {
  fill(ctx, DARK, x, y, w, h);
  fill(ctx, IRON[2], x, y, 1, h);
  fill(ctx, IRON[2], x, y, w, 1);
}

/** A handwheel valve: a ring with spokes and a hub, face on. */
function valve(ctx: Ctx, cx: number, cy: number, r: number, ramp: Ramp = IRON): void {
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + Math.PI / 6;
    line(ctx, ramp[0]!, cx, cy, cx + Math.cos(a) * r, cy + Math.sin(a) * r, 2.5);
    line(ctx, ramp[3]!, cx, cy, cx + Math.cos(a) * r, cy + Math.sin(a) * r, 1);
  }
  ring(ctx, ramp[0]!, cx, cy, r - 2.5, r + 0.5);
  ring(ctx, ramp[2]!, cx, cy, r - 2, r);
  ring(ctx, ramp[4]!, cx, cy, r - 1.5, r - 0.5);
  disc(ctx, ramp[0]!, cx, cy, r * 0.35 + 1);
  disc(ctx, ramp[3]!, cx, cy, r * 0.35);
}

/** A flanged steam inlet coming down from above: the pipe from the ship's boiler, with its bolted collar. */
function inlet(ctx: Ctx, cx: number, y: number, w: number, h: number, ramp: Ramp = IRON): void {
  pipe(ctx, [cx, y, cx, y + h], w, ramp);
  const fw = w + 6;
  panel(ctx, cx - fw / 2, y + h - 4, fw, 4, ramp[2]!, ramp[3]!, ramp[0]!);
  rivet(ctx, cx - fw / 2 + 1, y + h - 2);
  rivet(ctx, cx + fw / 2 - 3, y + h - 2);
}

/** A piston cylinder lying across (or standing up): a drum with bolted end caps and the rod out of one end. */
function cylinder(ctx: Ctx, x: number, y: number, w: number, h: number, ramp: Ramp, upright: boolean): void {
  drum(ctx, x, y, w, h, ramp, upright);
  if (upright) {
    panel(ctx, x - 2, y, w + 4, 4, ramp[2]!, ramp[3]!, ramp[0]!);
    panel(ctx, x - 2, y + h - 4, w + 4, 4, ramp[2]!, ramp[3]!, ramp[0]!);
    fill(ctx, IRON[3], x + w / 2 - 1, y - 5, 2, 6);
  } else {
    panel(ctx, x, y - 2, 4, h + 4, ramp[2]!, ramp[3]!, ramp[0]!);
    panel(ctx, x + w - 4, y - 2, 4, h + 4, ramp[2]!, ramp[3]!, ramp[0]!);
    fill(ctx, IRON[3], x + w, y + h / 2 - 1, 6, 2);
  }
}

/** A flyball governor standing on top of an engine: a spindle with two arms, a brass ball on each. */
function governor(ctx: Ctx, cx: number, base: number, h: number): void {
  fill(ctx, IRON[1], cx - 1, base - h, 2, h);
  for (const s of [-1, 1]) {
    line(ctx, IRON[2], cx, base - h + 2, cx + s * 6, base - h * 0.35, 1);
    disc(ctx, BRASS[0], cx + s * 6, base - h * 0.35, 2.5);
    disc(ctx, BRASS[3], cx + s * 6 - 0.5, base - h * 0.35 - 0.5, 1.5);
  }
  plate(ctx, cx - 3, base - 2, 6, 2);
}

/** A lever on a pivot at (px, py), `a` radians from it, `len` long, a round knob of `knob` at its end. */
function lever(ctx: Ctx, px: number, py: number, a: number, len: number, dark: string, light: string, knob: Ramp): void {
  const ex = px + Math.cos(a) * len;
  const ey = py + Math.sin(a) * len;
  line(ctx, dark, px, py, ex, ey, 4);
  line(ctx, light, px - 0.7, py, ex - 0.7, ey, 1.5);
  disc(ctx, knob[0]!, ex, ey, 5);
  disc(ctx, knob[2]!, ex, ey, 4);
  disc(ctx, knob[3]!, ex - 1, ey - 1, 2);
}

/** The toothed quadrant the levers rack along: an arc over the pivot, notched. */
function quadrant(ctx: Ctx, cx: number, cy: number, r0: number, r1: number, ramp: Ramp, span = 1.25): void {
  const within = (x: number, y: number, pad: number) => {
    const d = Math.hypot(x - cx, y - cy);
    return d >= r0 - pad && d <= r1 + pad && y < cy && Math.abs(Math.atan2(x - cx, cy - y)) <= span + pad * 0.04;
  };
  shape(ctx, ramp[0]!, cx - r1 - 1, cy - r1 - 1, cx + r1 + 1, cy + 1, (x, y) => within(x, y, 1));
  shape(ctx, ramp[2]!, cx - r1, cy - r1, cx + r1, cy, (x, y) => within(x, y, 0));
  for (let i = 0; i <= 10; i++) {
    const a = -Math.PI / 2 - span + (i / 10) * span * 2;
    line(ctx, ramp[0]!, cx + Math.cos(a) * r0, cy + Math.sin(a) * r0, cx + Math.cos(a) * r1, cy + Math.sin(a) * r1, 1);
  }
  shape(ctx, ramp[3]!, cx - r1, cy - r1, cx + r1, cy, (x, y) => within(x, y, 0) && Math.hypot(x - cx, y - cy) >= r1 - 1.5);
}

/** A pipe `w` thick through the points [x0, y0, x1, y1, ...], a highlight along its upper left. */
function pipe(ctx: Ctx, pts: readonly number[], w: number, ramp: Ramp = IRON): void {
  path(ctx, ramp[0]!, pts, w);
  path(ctx, ramp[2]!, pts, w - 2);
  path(ctx, ramp[3]!, pts.map((v) => v - 1), 1);
}

/** A chimney stack `w` wide from (x, y) down `h`, with a lip round its top. */
function stack(ctx: Ctx, x: number, y: number, w: number, h: number, ramp: Ramp = IRON): void {
  drum(ctx, x, y, w, h, ramp, true);
  fill(ctx, ramp[1]!, x - 1, y, w + 2, 3);
  fill(ctx, ramp[3]!, x - 1, y, w + 2, 1);
  fill(ctx, SOOT, x, y, w, 1);
}

/** A steam dome: the top half of a ball of `ramp`, `r` round, its base on `base`. */
function dome(ctx: Ctx, cx: number, base: number, r: number, ramp: Ramp): void {
  pixels(
    ctx,
    r * 2,
    r,
    (px, py) => {
      const dx = (px + 0.5 - r) / r;
      const dy = (py + 0.5 - r) / r;
      const d2 = dx * dx + dy * dy;
      if (d2 > 1) return null;
      return tone(ramp, 0.4 + (-dx * 0.4 - dy * 0.35 + Math.sqrt(1 - d2) * 0.45) * 0.7, px, py);
    },
    Math.round(cx - r),
    base - r,
  );
}

/** Rope lashed round something: a band of twisted strands, `upright` if the band stands (strands across it). */
function lashing(ctx: Ctx, x: number, y: number, w: number, h: number, upright: boolean): void {
  pixels(
    ctx,
    w,
    h,
    (px, py) => {
      const a = upright ? px : py;
      const n = upright ? w : h;
      if (a === 0 || a === n - 1) return ROPE[0];
      return (px + py) % 4 < 2 ? ROPE[2] : ROPE[1];
    },
    x,
    y,
  );
}

/** A coil of rope hung up: rings of it, `r0` to `r1`. */
function coil(ctx: Ctx, cx: number, cy: number, r0: number, r1: number): void {
  ring(ctx, ROPE[0], cx, cy, r0, r1);
  ring(ctx, ROPE[2], cx, cy, r0 + 1, r1 - 1);
  for (let r = r0 + 2.5; r < r1 - 1.5; r += 2.5) ring(ctx, ROPE[1], cx, cy, r, r + 0.8);
}

/** A barrel: upright staves of `ramp` shaded round, hoops of `hoopRamp` at these fractions down it. */
function cask(ctx: Ctx, x: number, y: number, w: number, h: number, hoops: readonly number[], ramp: Ramp = WOOD, hoopRamp: Ramp = IRON): void {
  pixels(
    ctx,
    w,
    h,
    (px, py) => {
      const u = (px + 0.5) / w;
      let k = 0.2 + Math.sin(Math.PI * u) * 0.7 - u * 0.1 + grain(py, px, 5) * 0.6;
      const stave = px % 6;
      if (stave === 5) return ramp[0]!;
      if (stave === 0) k += 0.12;
      return tone(ramp, k, px, py, 0.4);
    },
    x,
    y,
  );
  for (const f of hoops) hoop(ctx, x - 1, y + Math.round(f * (h - 4)), w + 2, 4, hoopRamp, true);
}

/** A glass tube showing water: dark frame, glass above the water line, water below. */
function gauge(ctx: Ctx, x: number, y: number, w: number, h: number, level: number): void {
  panel(ctx, x - 2, y - 2, w + 4, h + 4, "#1a1c1f", IRON[0], IRON[3]);
  const top = Math.round(h * (1 - level));
  glassPane(ctx, x, y, w, top);
  fill(ctx, WATER[1], x, y + top, w, h - top);
  fill(ctx, WATER[2], x, y + top, w, 1);
  fill(ctx, WATER[0], x + w - 1, y + top, 1, h - top);
}

// ----- engines: small (1x1), medium (2x2), large (3x3), all exhaust down

// Engines burn nothing: steam comes down a pipe from the ship's furnace and boiler, so each is a valve, a cylinder or two, and a nozzle.
const ENGINE_SMALL: PartDesign[] = [
  {
    family: "ironclad",
    w: 1,
    h: 1,
    blurb: "A squat armoured box with the steam inlet bolted on top, a handwheel valve on its face and a vent slot either side, over a flared iron nozzle.",
    paint: (ctx) => {
      inlet(ctx, 16, 0, 4, 5, ARMOUR);
      armour(ctx, 2, 4, 28, 17);
      valve(ctx, 16, 12, 5, ARMOUR);
      vent(ctx, 5, 8, 3, 9);
      vent(ctx, 24, 8, 3, 9);
      bell(ctx, 16, 21, 12, 20, 11, ARMOUR);
    },
  },
  {
    family: "brassworks",
    w: 1,
    h: 1,
    blurb: "A brass steam chest lying across, fed from a copper inlet on top, two copper hoops and a pressure dial on its face, a wide brass bell for a nozzle.",
    paint: (ctx) => {
      plate(ctx, 11, 17, 10, 5);
      inlet(ctx, 16, 0, 4, 4, COPPER);
      drum(ctx, 3, 3, 26, 15, BRASS, false);
      fill(ctx, BRASS[1], 3, 3, 1, 15);
      fill(ctx, BRASS[0], 28, 3, 1, 15);
      hoop(ctx, 7, 2, 3, 17, COPPER, false);
      hoop(ctx, 22, 2, 3, 17, COPPER, false);
      dial(ctx, 16, 10, 5);
      bell(ctx, 16, 21, 8, 20, 11, BRASS);
    },
  },
  {
    family: "shipwright",
    w: 1,
    h: 1,
    blurb: "An iron steam chest lashed with rope into a timber cradle, the pipe in through the top, a handwheel in its side, a short iron nozzle under the crossbeam.",
    paint: (ctx) => {
      beam(ctx, 2, 0, 4, 25);
      beam(ctx, 26, 0, 4, 25);
      inlet(ctx, 16, 0, 4, 5);
      drum(ctx, 6, 4, 20, 14, IRON, false);
      lashing(ctx, 8, 3, 3, 16, true);
      lashing(ctx, 21, 3, 3, 16, true);
      valve(ctx, 16, 11, 4);
      beam(ctx, 2, 20, 28, 5);
      bell(ctx, 16, 25, 8, 14, 7, IRON);
    },
  },
];

const ENGINE_MEDIUM: PartDesign[] = [
  {
    family: "ironclad",
    w: 2,
    h: 2,
    blurb: "Twin steam inlets bolted into a riveted housing, a big handwheel valve in the middle and an upright piston cylinder either side; two nozzles underneath.",
    paint: (ctx) => {
      for (const x of [16, 48]) inlet(ctx, x, 0, 6, 9, ARMOUR);
      armour(ctx, 4, 6, 56, 38);
      fill(ctx, ARMOUR[1], 4, 25, 56, 1);
      rivets(ctx, 12, 24, 52, 24, 8);
      for (const x of [8, 46]) cylinder(ctx, x, 12, 10, 26, IRON, true);
      valve(ctx, 32, 24, 9, ARMOUR);
      vent(ctx, 26, 38, 12, 3);
      plate(ctx, 20, 44, 24, 5);
      bell(ctx, 18, 44, 12, 20, 19, ARMOUR);
      bell(ctx, 46, 44, 12, 20, 19, ARMOUR);
    },
  },
  {
    family: "brassworks",
    w: 2,
    h: 2,
    blurb: "An upright brass steam chest under a dome, a copper collar and hoop, copper feed pipes down both flanks, a dial over a copper piston cylinder, one big bell below.",
    paint: (ctx) => {
      dome(ctx, 32, 10, 10, BRASS);
      plate(ctx, 10, 50, 44, 4);
      for (const [x, fx] of [[8, 5], [56, 53]] as const) {
        pipe(ctx, [x, 20, x, 48, x < 32 ? 16 : 48, 48], 4, COPPER);
        plate(ctx, fx, 18, 7, 3);
      }
      drum(ctx, 14, 8, 36, 44, BRASS, true);
      hoop(ctx, 13, 9, 38, 4, COPPER, true);
      hoop(ctx, 13, 42, 38, 4, COPPER, true);
      dial(ctx, 32, 21, 5);
      cylinder(ctx, 20, 31, 24, 9, COPPER, false);
      bell(ctx, 32, 54, 20, 40, 10, BRASS);
    },
  },
  {
    family: "shipwright",
    w: 2,
    h: 2,
    blurb: "A barrel of a steam chest, timber staves in iron hoops, framed in beams lashed at the corners, an iron plate low in its belly with a sight glass and a handwheel.",
    paint: (ctx) => {
      cask(ctx, 12, 6, 40, 40, [0.1, 0.5, 0.9]);
      panel(ctx, 22, 26, 20, 16, IRON[1], IRON[3], IRON[0]);
      gauge(ctx, 25, 28, 5, 12, 0.6);
      valve(ctx, 36, 34, 4.5);
      beam(ctx, 2, 0, 6, 52);
      beam(ctx, 56, 0, 6, 52);
      beam(ctx, 2, 0, 60, 5);
      beam(ctx, 2, 46, 60, 6);
      for (const [x, y] of [[1, 6], [55, 6], [1, 40], [55, 40]] as const) lashing(ctx, x, y, 8, 4, false);
      inlet(ctx, 32, 0, 6, 8);
      bell(ctx, 32, 52, 14, 24, 12, IRON);
    },
  },
];

const ENGINE_LARGE: PartDesign[] = [
  {
    family: "ironclad",
    w: 3,
    h: 3,
    blurb: "A main steam inlet bolted into a two-plate armoured block, a pressure cylinder set into each end, a great handwheel valve between them over a piston cylinder; three nozzles under a shroud.",
    paint: (ctx) => {
      inlet(ctx, 48, 0, 10, 13, ARMOUR);
      brass(ctx, 86, 4, 5, 8);
      armour(ctx, 4, 10, 88, 60);
      fill(ctx, ARMOUR[1], 4, 40, 88, 1);
      rivets(ctx, 12, 39, 84, 39, 9);
      for (const x of [10, 72]) {
        drum(ctx, x, 16, 14, 48, IRON, true);
        hoop(ctx, x - 1, 22, 16, 3, IRON, true);
        hoop(ctx, x - 1, 55, 16, 3, IRON, true);
      }
      valve(ctx, 48, 30, 12, ARMOUR);
      cylinder(ctx, 32, 50, 32, 12, IRON, false);
      plate(ctx, 8, 70, 80, 6);
      for (const cx of [20, 48, 76]) bell(ctx, cx, 70, 14, 22, 24, ARMOUR);
    },
  },
  {
    family: "brassworks",
    w: 3,
    h: 3,
    blurb: "A long brass steam chest with a dome, two whistles and a flyball governor, copper hoops at the ends, a copper handwheel between an inspection hatch and a dial, copper pipes down the flanks; a great bell between two small ones.",
    paint: (ctx) => {
      dome(ctx, 48, 16, 12, BRASS);
      for (const x of [34, 60]) {
        fill(ctx, BRASS[1], x, 8, 3, 8);
        fill(ctx, BRASS[3], x, 8, 1, 8);
        fill(ctx, BRASS[0], x - 1, 7, 5, 2);
      }
      governor(ctx, 22, 15, 12);
      pipe(ctx, [12, 50, 4, 50, 4, 66, 16, 66], 4, COPPER);
      pipe(ctx, [84, 50, 92, 50, 92, 66, 80, 66], 4, COPPER);
      drum(ctx, 8, 14, 80, 40, BRASS, false);
      fill(ctx, BRASS[1], 8, 14, 1, 40);
      fill(ctx, BRASS[0], 87, 14, 1, 40);
      hoop(ctx, 14, 13, 4, 42, COPPER, false);
      hoop(ctx, 78, 13, 4, 42, COPPER, false);
      panel(ctx, 22, 27, 16, 14, BRASS[2], BRASS[4], BRASS[0]);
      for (const [x, y] of [[24, 29], [34, 29], [24, 37], [34, 37]] as const) rivet(ctx, x, y);
      dial(ctx, 66, 34, 6);
      valve(ctx, 48, 34, 7, COPPER);
      brass(ctx, 8, 56, 80, 6);
      rivets(ctx, 12, 58, 84, 58, 12);
      bell(ctx, 48, 62, 24, 40, 32, BRASS);
      bell(ctx, 18, 62, 10, 16, 26, BRASS);
      bell(ctx, 78, 62, 10, 16, 26, BRASS);
    },
  },
  {
    family: "shipwright",
    w: 3,
    h: 3,
    blurb: "A timber engine house fed by a pipe through its roof, its front open on an iron steam chest with a sight glass and handwheel under it, a coil of line on the wall; twin iron nozzles below the sill.",
    paint: (ctx) => {
      inlet(ctx, 72, 0, 6, 14);
      planks(ctx, 6, 12, 84, 54, 7, true, 31);
      fill(ctx, "#15110d", 18, 18, 60, 44);
      drum(ctx, 20, 20, 56, 26, IRON, false);
      hoop(ctx, 30, 19, 3, 28, IRON, false);
      hoop(ctx, 62, 19, 3, 28, IRON, false);
      panel(ctx, 30, 46, 36, 16, IRON[1], IRON[3], IRON[0]);
      gauge(ctx, 35, 48, 6, 12, 0.55);
      valve(ctx, 54, 54, 5);
      beam(ctx, 14, 14, 68, 4);
      beam(ctx, 14, 14, 4, 50);
      beam(ctx, 78, 14, 4, 50);
      coil(ctx, 88, 36, 1.5, 5.5);
      beam(ctx, 2, 8, 92, 6);
      beam(ctx, 2, 64, 92, 6);
      beam(ctx, 2, 8, 6, 62);
      beam(ctx, 88, 8, 6, 62);
      for (const cx of [30, 66]) bell(ctx, cx, 70, 16, 26, 24, IRON);
    },
  },
];

// ----- the rest of the machinery

const FURNACE: PartDesign[] = [
  {
    family: "ironclad",
    w: 2,
    h: 2,
    blurb: "A square armoured firebox with a flue up the back, three bars across the fire and an ash slot below it.",
    paint: (ctx) => {
      backWalls(ctx, 2, 2);
      stack(ctx, 40, 0, 12, 12, ARMOUR);
      armour(ctx, 4, 8, 56, 50);
      fill(ctx, ARMOUR[1], 4, 20, 56, 1);
      grate(ctx, 16, 24, 32, 24, 3);
      fill(ctx, DARK, 18, 51, 28, 4);
      fill(ctx, FIRE[1], 20, 52, 24, 1);
      panel(ctx, 2, 58, 60, 6, ARMOUR[1], ARMOUR[2], ARMOUR[0]);
    },
  },
  {
    family: "brassworks",
    w: 2,
    h: 2,
    blurb: "An upright brass boiler with copper hoops, a round fire door with a brass rim, a dial and a copper flue.",
    paint: (ctx) => {
      backWalls(ctx, 2, 2);
      stack(ctx, 22, 0, 10, 10, COPPER);
      plate(ctx, 4, 56, 56, 6);
      drum(ctx, 6, 8, 52, 48, BRASS, true);
      hoop(ctx, 5, 12, 54, 4, COPPER, true);
      hoop(ctx, 5, 48, 54, 4, COPPER, true);
      fireEye(ctx, 32, 36, 11, BRASS, false);
      dial(ctx, 49, 23, 4.5);
      rivets(ctx, 8, 58, 54, 58, 11);
      fill(ctx, DARK, 18, 50, 28, 3);
    },
  },
  {
    family: "shipwright",
    w: 2,
    h: 2,
    blurb: "A stone hearth: a kiln of mortared rock with an arched iron-framed firebox and an iron flue cap on top.",
    paint: (ctx) => {
      backWalls(ctx, 2, 2);
      stack(ctx, 38, 0, 12, 12);
      pixels(ctx, 56, 52, stoneField(ROCK, 8, 61, { seam: 1.4, relief: 0.3 }), 4, 10);
      fill(ctx, ROCK[0], 4, 10, 56, 1);
      beam(ctx, 2, 58, 60, 6);
      // The arch of the firebox, framed in iron.
      shape(ctx, IRON[1], 12, 20, 52, 50, (x, y) => y > 30 || Math.hypot(x - 32, y - 30) < 20);
      shape(ctx, DARK, 15, 23, 49, 50, (x, y) => y > 30 || Math.hypot(x - 32, y - 30) < 17);
      grate(ctx, 16, 28, 32, 20, 2);
      fill(ctx, IRON[1], 12, 48, 40, 2);
      fill(ctx, IRON[3], 12, 48, 40, 1);
    },
  },
];

const PUMP: PartDesign[] = [
  {
    family: "ironclad",
    w: 1,
    h: 1,
    blurb: "An iron pump housing with the main up through it, a lever arm cocked to one side and a sight glass showing the water.",
    paint: (ctx) => {
      armour(ctx, 0, 0, 32, 32);
      drum(ctx, 12, 0, 8, 32, IRON, true);
      for (const y of [2, 26]) plate(ctx, 10, y, 12, 4);
      gauge(ctx, 4, 10, 4, 12, 0.6);
      line(ctx, ARMOUR[0], 16, 18, 27, 7, 3);
      line(ctx, ARMOUR[3], 16, 18, 27, 7, 1);
      disc(ctx, ARMOUR[0], 27, 7, 3);
      disc(ctx, ARMOUR[3], 27, 7, 2);
      disc(ctx, ARMOUR[1], 16, 18, 3);
    },
  },
  {
    family: "brassworks",
    w: 1,
    h: 1,
    blurb: "A copper main across the block with a brass pump dome over it, a dial on the dome and a brass valve wheel at the end.",
    paint: (ctx) => {
      plate(ctx, 0, 0, 32, 32);
      rivets(ctx, 2, 2, 28, 2, 26);
      rivets(ctx, 2, 28, 28, 28, 26);
      dome(ctx, 13, 18, 9, BRASS);
      drum(ctx, 0, 17, 32, 9, COPPER, false);
      for (const x of [3, 22]) plate(ctx, x, 16, 4, 11);
      dial(ctx, 13, 13, 4);
      ring(ctx, BRASS[0], 25, 10, 3.5, 6);
      ring(ctx, BRASS[3], 25, 10, 4, 5);
      line(ctx, BRASS[3], 20, 10, 30, 10, 1);
      line(ctx, BRASS[3], 25, 5, 25, 15, 1);
      disc(ctx, BRASS[4], 25, 10, 1.5);
    },
  },
  {
    family: "shipwright",
    w: 1,
    h: 1,
    blurb: "A ship's pump: a hollowed log bound in iron hoops over a timber box, a long handspike to work it, water slopping from the spout.",
    paint: (ctx) => {
      panel(ctx, 2, 20, 28, 12, WOOD[2], WOOD[3], WOOD[0]);
      planks(ctx, 3, 21, 26, 10, 5, false, 17);
      drum(ctx, 11, 0, 10, 22, WOOD, true);
      hoop(ctx, 10, 2, 12, 3, IRON, true);
      hoop(ctx, 10, 16, 12, 3, IRON, true);
      line(ctx, WOOD[1], 15, 6, 30, 1, 3);
      line(ctx, WOOD[3], 15, 6, 30, 1, 1);
      plate(ctx, 13, 4, 6, 4);
      fill(ctx, IRON[1], 6, 10, 6, 3);
      fill(ctx, WATER[1], 6, 13, 3, 7);
      fill(ctx, WATER[2], 6, 13, 1, 7);
    },
  },
];

const BOW_GUN: PartDesign[] = [
  {
    family: "ironclad",
    w: 1,
    h: 1,
    blurb: "A casemate: sloped armour with the gun out through a round port, thick and short.",
    paint: (ctx) => {
      poly(ctx, ARMOUR[2], [0, 0, 14, 0, 20, 6, 20, 26, 14, 32, 0, 32]);
      poly(ctx, ARMOUR[3], [0, 0, 14, 0, 20, 6, 19, 7, 13, 1, 1, 1, 1, 31, 0, 32]);
      poly(ctx, ARMOUR[0], [20, 7, 20, 26, 14, 32, 13, 31, 19, 25, 19, 7]);
      rivets(ctx, 3, 3, 3, 27, 8);
      rivets(ctx, 10, 3, 10, 27, 8);
      disc(ctx, DARK, 17, 16, 5.5);
      drum(ctx, 14, 12, 16, 8, IRON, false);
      fill(ctx, IRON[0], 20, 12, 1, 8);
      plate(ctx, 27, 10, 5, 12);
      fill(ctx, SOOT, 30, 13, 2, 6);
    },
  },
  {
    family: "brassworks",
    w: 1,
    h: 1,
    blurb: "A brass swivel gun on an iron pintle: a long polished barrel with a flared muzzle and a brass handle on the breech.",
    paint: (ctx) => {
      plate(ctx, 4, 20, 12, 12);
      rivet(ctx, 6, 28);
      rivet(ctx, 12, 28);
      drum(ctx, 8, 14, 5, 8, IRON, true);
      drum(ctx, 4, 10, 24, 8, BRASS, false);
      drum(ctx, 26, 8, 6, 12, BRASS, false);
      fill(ctx, SOOT, 30, 11, 2, 6);
      hoop(ctx, 14, 9, 3, 10, COPPER, false);
      disc(ctx, BRASS[1], 4, 14, 4);
      disc(ctx, BRASS[3], 3, 13, 2);
      line(ctx, BRASS[0], 2, 14, 0, 21, 2);
      disc(ctx, BRASS[3], 1, 21, 1.5);
    },
  },
  {
    family: "shipwright",
    w: 1,
    h: 1,
    blurb: "A carronade: a fat black barrel on a timber sled, rope breeching round the breech.",
    paint: (ctx) => {
      beam(ctx, 2, 22, 26, 6);
      beam(ctx, 6, 16, 14, 6);
      beam(ctx, 4, 28, 22, 4);
      drum(ctx, 6, 8, 20, 10, ["#141517", "#24262a", "#3a3d42", "#50545a", "#6c7178"], false);
      drum(ctx, 24, 6, 8, 14, ["#141517", "#24262a", "#3a3d42", "#50545a", "#6c7178"], false);
      fill(ctx, SOOT, 29, 9, 3, 8);
      lashing(ctx, 8, 7, 3, 12, true);
      disc(ctx, IRON[1], 5, 13, 3);
      coil(ctx, 4, 20, 1, 4);
    },
  },
];

const WINCH: PartDesign[] = [
  {
    family: "ironclad",
    w: 2,
    h: 2,
    blurb: "A steel cable on an iron drum between armoured cheeks, a toothed wheel and pawl on one side.",
    paint: (ctx) => {
      backWalls(ctx, 2, 2);
      pixels(ctx, 4, 22, (x, y) => (y % 2 ? IRON[3] : IRON[1]), 30, 0);
      armour(ctx, 4, 12, 10, 46);
      armour(ctx, 50, 12, 10, 46);
      pixels(ctx, 36, 24, (x, y) => (y % 4 === 3 ? IRON[0] : (x + y) % 3 ? IRON[3] : IRON[2]), 14, 20);
      fill(ctx, IRON[1], 14, 20, 36, 1);
      fill(ctx, IRON[0], 14, 43, 36, 1);
      ring(ctx, ARMOUR[0], 7, 32, 5, 10);
      shape(ctx, ARMOUR[2], -4, 21, 18, 43, (x, y) => {
        const d = Math.hypot(x - 7, y - 32);
        const a = Math.atan2(y - 32, x - 7);
        return d < 9 && (d < 7 || Math.abs(((a * 8) / Math.PI) % 1) < 0.5);
      });
      line(ctx, ARMOUR[0], 7, 22, 14, 14, 3);
      line(ctx, ARMOUR[3], 7, 22, 14, 14, 1);
      panel(ctx, 2, 58, 60, 6, ARMOUR[1], ARMOUR[2], ARMOUR[0]);
    },
  },
  {
    family: "brassworks",
    w: 2,
    h: 2,
    blurb: "A rope drum between brass cheeks, a spoked brass crank wheel, a dial on the frame.",
    paint: (ctx) => {
      backWalls(ctx, 2, 2);
      pixels(ctx, 4, 22, (x, y) => ((x + y) % 4 < 2 ? ROPE[2] : ROPE[1]), 30, 0);
      brass(ctx, 6, 14, 8, 44);
      brass(ctx, 50, 14, 8, 44);
      pixels(ctx, 36, 24, (x, y) => (y % 3 === 2 ? ROPE[0] : (x + y) % 5 < 2 ? ROPE[3] : y % 3 === 0 ? ROPE[2] : ROPE[1]), 14, 20);
      fill(ctx, WOOD[1], 14, 20, 36, 1);
      fill(ctx, WOOD[0], 14, 43, 36, 1);
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        line(ctx, BRASS[1], 58, 32, 58 + Math.cos(a) * 11, 32 + Math.sin(a) * 11, 2);
      }
      ring(ctx, BRASS[0], 58, 32, 9, 12);
      ring(ctx, BRASS[3], 58, 32, 9.5, 11);
      disc(ctx, BRASS[3], 58, 32, 3);
      dial(ctx, 10, 6, 4);
      plate(ctx, 2, 58, 60, 6);
    },
  },
  {
    family: "shipwright",
    w: 2,
    h: 2,
    blurb: "A timber windlass: a rope-wound log between oak cheeks with iron corner brackets, a long handspike to turn it.",
    paint: (ctx) => {
      backWalls(ctx, 2, 2);
      pixels(ctx, 4, 22, (x, y) => ((x + y) % 4 < 2 ? ROPE[2] : ROPE[1]), 30, 0);
      beam(ctx, 4, 12, 8, 46);
      beam(ctx, 52, 12, 8, 46);
      for (const [x, y] of [[4, 12], [52, 12], [4, 52], [52, 52]] as const) {
        plate(ctx, x, y, 8, 6);
        rivet(ctx, x + 3, y + 2);
      }
      drum(ctx, 12, 18, 40, 28, WOOD, false);
      pixels(ctx, 30, 24, (x, y) => (y % 3 === 2 ? ROPE[0] : (x + y) % 5 < 2 ? ROPE[3] : y % 3 === 0 ? ROPE[2] : ROPE[1]), 17, 20);
      line(ctx, WOOD[1], 56, 32, 62, 4, 4);
      line(ctx, WOOD[3], 56, 32, 62, 4, 2);
      disc(ctx, IRON[1], 56, 32, 4);
      disc(ctx, IRON[3], 56, 32, 2);
      beam(ctx, 2, 58, 60, 6);
    },
  },
];

/** A turret's or harpoon's seat, the lower half of a 4x4 piece: dark, framed in the family's way, a bench under the gun mount. */
function seat(ctx: Ctx, family: Family): void {
  const W = S * 4;
  const half = W / 2;
  pixels(ctx, W, half, (x, y) => tone(["#16140f", "#1f1c18", "#2a2622", "#332e29"], 0.65 + (noise(x, y, 8, 5) - 0.5) * 0.4 - (y / half) * 0.4, x, y), 0, half);
  if (family === "shipwright") {
    beam(ctx, 0, half, 8, half);
    beam(ctx, W - 8, half, 8, half);
    beam(ctx, 22, half + 36, W - 44, 7);
    beam(ctx, 28, half + 43, 6, half - 43);
    beam(ctx, W - 34, half + 43, 6, half - 43);
    beam(ctx, half - 6, half, 12, 36);
    plate(ctx, half - 8, half + 30, 16, 6);
    return;
  }
  if (family === "ironclad") {
    armour(ctx, 0, half, 10, half);
    armour(ctx, W - 10, half, 10, half);
  } else {
    plate(ctx, 0, half, 8, half);
    plate(ctx, W - 8, half, 8, half);
    brass(ctx, 0, half, 8, 4);
    brass(ctx, W - 8, half, 8, 4);
  }
  beam(ctx, 22, half + 36, W - 44, 7);
  plate(ctx, 28, half + 43, 6, half - 43);
  plate(ctx, W - 34, half + 43, 6, half - 43);
  panel(ctx, half - 6, half, 12, 36, IRON[1], IRON[2], IRON[0]);
  if (family === "brassworks") brass(ctx, half - 8, half + 30, 16, 6);
}

const TURRET: PartDesign[] = [
  {
    family: "ironclad",
    w: 4,
    h: 4,
    blurb: "A casemate turret: a flat-fronted armoured box with sloped shoulders, a wide vision slit and rivets along every plate.",
    paint: (ctx) => {
      const W = S * 4;
      const half = W / 2;
      seat(ctx, "ironclad");
      poly(ctx, ARMOUR[2], [10, half, 10, 22, 30, 6, W - 30, 6, W - 10, 22, W - 10, half]);
      poly(ctx, ARMOUR[3], [10, half, 10, 22, 30, 6, W - 30, 6, W - 31, 8, 31, 8, 12, 23, 12, half]);
      poly(ctx, ARMOUR[0], [W - 30, 6, W - 10, 22, W - 10, half, W - 12, half, W - 12, 23, W - 31, 8]);
      fill(ctx, ARMOUR[0], 30, 24, W - 60, 1);
      fill(ctx, ARMOUR[0], 30, 44, W - 60, 1);
      rivets(ctx, 34, 12, W - 36, 12, 12);
      rivets(ctx, 16, 28, 16, half - 14, 10);
      rivets(ctx, W - 18, 28, W - 18, half - 14, 10);
      fill(ctx, DARK, half - 28, 30, 56, 9);
      fill(ctx, "#3a4a52", half - 26, 31, 20, 1);
      panel(ctx, 4, half - 10, W - 8, 10, ARMOUR[2], ARMOUR[3], ARMOUR[0]);
      rivets(ctx, 10, half - 7, W - 12, half - 7, 12);
    },
  },
  {
    family: "brassworks",
    w: 4,
    h: 4,
    blurb: "A brass cupola with copper hoops, two round portholes and a finial on the crown, on a riveted brass base ring.",
    paint: (ctx) => {
      const W = S * 4;
      const half = W / 2;
      seat(ctx, "brassworks");
      const R = half - 4;
      pixels(ctx, W, half, (x, y) => {
        const dx = (x + 0.5 - half) / R;
        const dy = (y + 0.5 - half) / R;
        const d2 = dx * dx + dy * dy;
        if (d2 > 1) return null;
        const r = Math.sqrt(d2);
        const k = 0.42 + (-dx * 0.45 - dy * 0.35 + Math.sqrt(1 - d2) * 0.4) * 0.6;
        if (Math.abs(r - 0.6) * R < 2.5 || r > 0.93) return tone(COPPER, k, x, y);
        return tone(BRASS, k, x, y);
      });
      for (const x of [half - 22, half + 22]) {
        ring(ctx, BRASS[0], x, half - 24, 6, 8.5);
        ring(ctx, BRASS[3], x, half - 24, 6.5, 7.5);
        shape(ctx, GLASS[2], x - 6, half - 30, x + 6, half - 18, (px, py) => Math.hypot(px - x, py - half + 24) < 6);
        fill(ctx, GLASS[4], x - 3, half - 27, 2, 2);
      }
      fill(ctx, BRASS[1], half - 2, 0, 4, 8);
      disc(ctx, BRASS[3], half, 1, 2.5);
      brass(ctx, 0, half - 10, W, 10);
      rivets(ctx, 6, half - 7, W - 8, half - 7, 12);
    },
  },
  {
    family: "shipwright",
    w: 4,
    h: 4,
    blurb: "A timber fighting top: stave walls bound in iron, a gun port with a hinged lid, a rope coil hung on the side.",
    paint: (ctx) => {
      const W = S * 4;
      const half = W / 2;
      seat(ctx, "shipwright");
      pixels(ctx, W - 16, half - 12, (x, y) => {
        const u = (x + 0.5) / (W - 16);
        let k = 0.3 + Math.sin(Math.PI * u) * 0.55 + grain(y, x, 8) * 0.6;
        if (x % 9 === 8) return WOOD[0];
        if (x % 9 === 0) k += 0.12;
        return tone(WOOD, k, x, y, 0.4);
      }, 8, 12);
      beam(ctx, 6, 8, W - 12, 5);
      for (const y of [16, half - 22]) {
        drum(ctx, 7, y, W - 14, 5, IRON, true);
        fill(ctx, "#1a1612", 7, y + 5, W - 14, 1);
      }
      rivets(ctx, 12, 17, W - 16, 17, 14);
      fill(ctx, DARK, half - 22, 28, 44, 12);
      beam(ctx, half - 24, 24, 48, 4);
      coil(ctx, W - 22, half - 12, 2, 8);
      plate(ctx, 0, half - 10, W, 10);
      rivets(ctx, 6, half - 7, W - 8, half - 7, 12);
    },
  },
];

const HARPOON: PartDesign[] = [
  {
    family: "ironclad",
    w: 4,
    h: 4,
    blurb: "A low armoured box with a wide launch slot and a steel cable reel on its flank (the launcher swings on top).",
    paint: (ctx) => {
      const W = S * 4;
      const half = W / 2;
      seat(ctx, "ironclad");
      armour(ctx, 8, 20, W - 16, half - 20);
      fill(ctx, ARMOUR[1], 8, 36, W - 16, 1);
      fill(ctx, DARK, half - 26, 26, 52, 7);
      ring(ctx, IRON[0], W - 30, 46, 3, 12);
      pixels(ctx, 24, 24, (x, y) => {
        const d = Math.hypot(x + 0.5 - 12, y + 0.5 - 12);
        return d > 3 && d < 11 ? (Math.round(d) % 2 ? IRON[3] : IRON[1]) : null;
      }, W - 42, 34);
      disc(ctx, ARMOUR[1], W - 30, 46, 3);
      panel(ctx, 4, half - 10, W - 8, 10, ARMOUR[2], ARMOUR[3], ARMOUR[0]);
      rivets(ctx, 10, half - 7, W - 12, half - 7, 12);
    },
  },
  {
    family: "brassworks",
    w: 4,
    h: 4,
    blurb: "A low brass dome with a copper band, a round lookout and a brass-cheeked line reel, a dial beside it.",
    paint: (ctx) => {
      const W = S * 4;
      const half = W / 2;
      seat(ctx, "brassworks");
      const R = half - 4;
      pixels(ctx, W, half, (x, y) => {
        const dx = (x + 0.5 - half) / R;
        const dy = (y + 0.5 - half) / (R * 0.7);
        const d2 = dx * dx + dy * dy;
        if (d2 > 1 || y + 0.5 < half - R * 0.7) return null;
        const r = Math.sqrt(d2);
        const k = 0.42 + (-dx * 0.45 - dy * 0.35 + Math.sqrt(1 - d2) * 0.4) * 0.6;
        if (Math.abs(r - 0.7) * R < 2.5 || r > 0.93) return tone(COPPER, k, x, y);
        return tone(BRASS, k, x, y);
      });
      ring(ctx, BRASS[0], half, half - 26, 5, 7.5);
      shape(ctx, GLASS[2], half - 5, half - 31, half + 5, half - 21, (px, py) => Math.hypot(px - half, py - half + 26) < 5);
      coil(ctx, W - 30, half - 16, 3, 12);
      brass(ctx, W - 32, half - 30, 4, 28);
      dial(ctx, 30, half - 18, 6);
      brass(ctx, 0, half - 10, W, 10);
      rivets(ctx, 6, half - 7, W - 8, half - 7, 12);
    },
  },
  {
    family: "shipwright",
    w: 4,
    h: 4,
    blurb: "An open timber platform: a railing of turned posts, a bollard with the line coiled on it, planks underfoot.",
    paint: (ctx) => {
      const W = S * 4;
      const half = W / 2;
      seat(ctx, "shipwright");
      for (const x of [10, 34, W - 40, W - 16]) beam(ctx, x, 22, 6, half - 22);
      beam(ctx, 6, 20, W - 12, 5);
      beam(ctx, 6, 40, W - 12, 4);
      beam(ctx, half - 6, 30, 12, half - 30);
      coil(ctx, half, 42, 4, 11);
      lashing(ctx, half - 7, 32, 14, 5, false);
      planks(ctx, 0, half - 10, W, 10, 9, false, 77);
      plate(ctx, 0, half - 10, W, 2);
    },
  },
];

const HELM: PartDesign[] = [
  {
    family: "ironclad",
    w: 4,
    h: 4,
    blurb: "A bank of four iron levers racked along a toothed quadrant on a riveted pedestal, the second one red-knobbed, steering pipes running down to the deck, a compass dome and a dial either side.",
    paint: (ctx) => {
      const W = S * 4;
      backWalls(ctx, 4, 4);
      const cx = W / 2;
      const py = 74;
      pipe(ctx, [cx - 14, 96, cx - 14, 112, cx - 30, 112, cx - 30, W], 4);
      pipe(ctx, [cx + 14, 96, cx + 14, 112, cx + 30, 112, cx + 30, W], 4);
      panel(ctx, 36, W - 12, W - 72, 12, ARMOUR[1], ARMOUR[3], ARMOUR[0]);
      armour(ctx, cx - 18, py, 36, W - py - 12);
      rivets(ctx, cx - 14, py + 6, cx + 10, py + 6, 12);
      quadrant(ctx, cx, py, 28, 35, ARMOUR);
      const RED = ["#4a1008", "#7a1c10", "#b8321c", "#e05a3a"] as const;
      [-0.95, -0.35, 0.25, 0.8].forEach((da, i) => lever(ctx, cx, py, -Math.PI / 2 + da, 46, ARMOUR[0], ARMOUR[3], i === 1 ? RED : ARMOUR));
      disc(ctx, ARMOUR[0], cx, py, 6);
      disc(ctx, ARMOUR[2], cx, py, 5);
      rivet(ctx, cx - 1, py - 1);
      dome(ctx, cx - 44, 30, 8, GLASS);
      fill(ctx, "#b8321c", cx - 45, 24, 2, 6);
      dial(ctx, cx + 44, 30, 6);
      dial(ctx, cx - 34, 100, 6);
      dial(ctx, cx + 34, 100, 6);
    },
  },
  {
    family: "brassworks",
    w: 4,
    h: 4,
    blurb: "Mahogany levers with brass knobs along a brass quadrant on a brass binnacle: a compass dome, a dial under the pivot and a lamp either side.",
    paint: (ctx) => {
      const W = S * 4;
      backWalls(ctx, 4, 4);
      const cx = W / 2;
      const py = 72;
      brass(ctx, 36, W - 12, W - 72, 12);
      rivets(ctx, 40, W - 8, W - 44, W - 8, 44);
      drum(ctx, cx - 10, py + 4, 20, W - py - 16, BRASS, true);
      hoop(ctx, cx - 12, py + 16, 24, 5, COPPER, true);
      hoop(ctx, cx - 12, W - 24, 24, 5, COPPER, true);
      const DARKWOOD = ["#2a1208", "#4a1e0e", "#6b2f16", "#8a4222", "#a85a30"] as const;
      quadrant(ctx, cx, py, 27, 33, BRASS);
      [-1.0, -0.5, 0, 0.45, 0.9].forEach((da) => lever(ctx, cx, py, -Math.PI / 2 + da, 44, DARKWOOD[1], DARKWOOD[3], BRASS));
      disc(ctx, BRASS[0], cx, py, 6);
      disc(ctx, BRASS[3], cx, py, 5);
      disc(ctx, BRASS[4], cx - 1.5, py - 1.5, 2);
      dome(ctx, cx - 44, 30, 8, GLASS);
      fill(ctx, "#b8321c", cx - 45, 24, 2, 6);
      for (const x of [cx - 34, cx + 34]) {
        brass(ctx, x - 3, 90, 6, 3);
        glassPane(ctx, x - 4, 93, 8, 10);
        fill(ctx, FIRE[3], x - 1, 96, 2, 5);
        brass(ctx, x - 3, 103, 6, 3);
      }
      dial(ctx, cx, 98, 6);
    },
  },
  {
    family: "shipwright",
    w: 4,
    h: 4,
    blurb: "Three oak levers with rope-wrapped grips through an iron quadrant plate on a stout timber pedestal, rope tackle running from the pivot down to the deck, a lantern hung beside it.",
    paint: (ctx) => {
      const W = S * 4;
      backWalls(ctx, 4, 4);
      const cx = W / 2;
      const py = 72;
      for (const x of [cx - 20, cx + 20]) pixels(ctx, 3, W - 12 - (py + 8), (px, p) => ((px + p) % 4 < 2 ? ROPE[2] : ROPE[1]), x - 1, py + 8);
      beam(ctx, 28, W - 10, W - 56, 10);
      beam(ctx, cx - 12, py + 4, 24, W - py - 14);
      plate(ctx, cx - 14, py + 18, 28, 5);
      plate(ctx, cx - 14, W - 22, 28, 5);
      quadrant(ctx, cx, py, 26, 32, IRON);
      const GRIP = ["#4a3a22", "#6e5a3a", "#9a8058", "#c8b088"] as const;
      for (const da of [-0.8, -0.1, 0.65]) {
        lever(ctx, cx, py, -Math.PI / 2 + da, 44, WOOD[1], WOOD[3], GRIP);
        // The rope grip, wound round the knob.
        const ex = cx + Math.cos(-Math.PI / 2 + da) * 44;
        const ey = py + Math.sin(-Math.PI / 2 + da) * 44;
        ring(ctx, ROPE[0], ex, ey, 3.4, 4);
        ring(ctx, ROPE[1], ex, ey, 1.9, 2.5);
      }
      disc(ctx, IRON[0], cx, py, 6);
      disc(ctx, IRON[2], cx, py, 5);
      rivet(ctx, cx - 1, py - 1);
      // A lantern hung from a peg.
      fill(ctx, IRON[1], W - 20, 24, 2, 8);
      plate(ctx, W - 24, 32, 10, 3);
      glassPane(ctx, W - 23, 35, 8, 12);
      fill(ctx, FIRE[3], W - 20, 39, 2, 6);
      plate(ctx, W - 24, 47, 10, 3);
    },
  },
];

export const SHIP_DESIGNS: PartGroup[] = [
  { id: "engine-s", title: "Small engine", note: "One block, as now. Exhaust down; the other three ways turn with T.", designs: ENGINE_SMALL },
  { id: "engine-m", title: "Medium engine", note: "A 2×2 piece. Exhaust along its bottom edge; the other ways would be their own pieces, turned with T.", designs: ENGINE_MEDIUM },
  { id: "engine-l", title: "Large engine", note: "A 3×3 piece. Exhaust along its bottom edge, three nozzles wide.", designs: ENGINE_LARGE },
  { id: "furnace", title: "Furnace", note: "2×2, in the background. The fire stays about where it is now, where GridMesh puts its glow.", designs: FURNACE },
  { id: "pump", title: "Pump", note: "One solid block.", designs: PUMP },
  { id: "bowgun", title: "Bow gun", note: "One solid block, firing right; the back-firing one mirrors it.", designs: BOW_GUN },
  { id: "winch", title: "Winch", note: "2×2, in the background, its line running up and out of the top.", designs: WINCH },
  { id: "turret", title: "Turret", note: "4×4: the seat below (background), the housing above (solid). The barrel swings on top (TurretBarrels).", designs: TURRET },
  { id: "harpoon", title: "Harpoon", note: "4×4, like a turret; the launcher swings on top.", designs: HARPOON },
  { id: "helm", title: "Helm", note: "4×4, in the background, in front of the back wall.", designs: HELM },
];

/** `d` turned `angle` radians clockwise about its middle, and mirrored left-right if `mirror`. */
function turned(d: PartDesign, angle: number, mirror = false): (ctx: Ctx) => void {
  return (ctx) => {
    const art = document.createElement("canvas");
    art.width = S * d.w;
    art.height = S * d.h;
    d.paint(art.getContext("2d")!);
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    ctx.translate(art.width / 2, art.height / 2);
    ctx.rotate(angle);
    if (mirror) ctx.scale(-1, 1);
    ctx.drawImage(art, -art.width / 2, -art.height / 2);
    ctx.restore();
  };
}

/** The ways an engine faces, by its blocks' names, and how far its exhaust-down art turns for each. */
const WAYS = [["DOWN", 0], ["LEFT", Math.PI / 2], ["UP", Math.PI], ["RIGHT", -Math.PI / 2]] as const;

/** The blocks each group paints, by their names in the registry without the family, each turned (and mirrored) so. */
export const BLOCKS_OF: Record<string, readonly (readonly [key: string, angle: number, mirror?: boolean])[]> = {
  "engine-s": WAYS.map(([way, a]) => [`ENGINE_${way}`, a] as const),
  "engine-m": WAYS.map(([way, a]) => [`MEDIUM_ENGINE_${way}`, a] as const),
  "engine-l": WAYS.map(([way, a]) => [`LARGE_ENGINE_${way}`, a] as const),
  furnace: [["FURNACE", 0]],
  pump: [["PUMP", 0]],
  bowgun: [["BOW_GUN", 0], ["BOW_GUN_BACK", 0, true]],
  winch: [["WINCH", 0]],
  turret: [["TURRET", 0]],
  harpoon: [["HARPOON", 0]],
  helm: [["HELM", 0]],
};

/** Every machine block's painter, by its name in the registry (IRONCLAD_FURNACE ...): painted whole, a piece's tiles and all. */
export const SHIP_PAINT: Readonly<Record<string, (ctx: Ctx, v: number) => void>> = Object.fromEntries(
  SHIP_DESIGNS.flatMap((g) => g.designs.flatMap((d) => BLOCKS_OF[g.id]!.map(([key, angle, mirror]) => [`${d.family.toUpperCase()}_${key}`, angle || mirror ? turned(d, angle, mirror) : d.paint]))),
);
